import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import MuiGrid from '@material-ui/core/Grid';
import MuiBox from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiFormHelperText from '@material-ui/core/FormHelperText';
import MuiFormControlLabel from '@material-ui/core/FormControlLabel';
import MuiFormGroup from '@material-ui/core/FormGroup';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import MuiCheckbox from '@material-ui/core/Checkbox';
import MuiTextField from '@material-ui/core/TextField';
import MuiSwitch from '@material-ui/core/Switch';

import ENUMS from '../../constants/ENUMS.gen.json';

import * as API from 'api';

// Expiration date should be set in PST by the end of the day.
const dateToExpirationDateTime = dateStr => `${dateStr}T23:59:59.999-08:00`;
const isoToExpirationEndDate = isoDateStr => DateTime.fromISO(isoDateStr).setZone('UTC-8').toISODate();

const Groups = API.Groups();
const ServiceDeals = API.ServiceDeals();

const isDateBeforeToday = date => new Date(date) < new Date();

const styles = theme => ({
  bold: {
    fontWeight: 'bold',
  },
  formElement: {
    margin: '6px',
    padding: '6px',
    width: '100%',
  },
  checkBoxElement: {
    margin: '3px',
    padding: '3px',
  },
  toggleLabel: {
    margin: '0.5rem',
  },
  form: {
    width: '100%',
  },
  error: {
    color: theme.palette.primary.error,
  },
});

function DealForm({
  classes,
  serviceDealId,
  selectedGroup,
  setSelectedGroup,
  selectedDealType,
  setSelectedDealType,
  dealName,
  setDealName,
  dealNotes,
  setDealNotes,
  opsId,
  setOpsId,
  isActive,
  setIsActive,
  expirationDate,
  setexpirationDate,
  associatedRecoupAccounts,
  setAssociatedRecoupAccounts,
  isExpirationDateValid,
}) {
  function groupSelected({ target: { value } }) {
    setSelectedGroup(value);
  }

  const { data: groups, isSuccess } = useQuery(
    ['groups'],
    () => Groups.getAllSegmented(),
    { refetchOnWindowFocus: false },
    { enabled: !selectedGroup }
  ); // TODO: error toast

  const { data: selectedGroupWithRecoup } = useQuery(
    ['groupsWithRecoup', selectedGroup],
    () => Groups.getWithRecoup(selectedGroup),
    { refetchOnWindowFocus: false },
    { enabled: !!selectedGroup }
  );

  const isExpired = isDateBeforeToday(expirationDate);

  return (
    <MuiGrid container>
      <MuiGrid item xs={3}>
        <span className={classes.bold}>Add Account Details</span>
        <p>
          Add the details of the service deal. <br />
          These details can be edited later
        </p>
      </MuiGrid>
      <MuiGrid item xs={6}>
        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Label</MuiFormHelperText>
            <>
              {isSuccess ? (
                <MuiSelect labelId="select-group" id="user_group_id" value={selectedGroup} onChange={groupSelected}>
                  <MuiMenuItem value={0}> -- Please Select A Group -- </MuiMenuItem>
                  {groups?.map(group => (
                    <MuiMenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MuiMenuItem>
                  ))}
                </MuiSelect>
              ) : (
                <MuiCircularProgress />
              )}
            </>
          </MuiFormControl>
        </MuiBox>
        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Deal Type</MuiFormHelperText>
            <>
              <MuiSelect
                labelId="select-deal-type"
                id="select-deal-type"
                value={selectedDealType}
                onChange={evt => {
                  setSelectedDealType(evt.target.value);
                  setAssociatedRecoupAccounts({});
                }}>
                <MuiMenuItem value={0}> -- Please Select A Deal Type -- </MuiMenuItem>
                {Object.values(ENUMS.SERVICE_DEAL_TYPE)?.map(dealType => (
                  <MuiMenuItem key={dealType} value={dealType}>
                    {dealType}
                  </MuiMenuItem>
                ))}
              </MuiSelect>
            </>
          </MuiFormControl>
        </MuiBox>
        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Name</MuiFormHelperText>
            <MuiTextField
              onChange={e => {
                setDealName(e.target.value);
              }}
              value={dealName}
            />
          </MuiFormControl>
        </MuiBox>

        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Ops ID</MuiFormHelperText>
            <MuiTextField
              onChange={e => {
                setOpsId(e.target.value);
              }}
              value={opsId}
            />
          </MuiFormControl>
        </MuiBox>

        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Notes</MuiFormHelperText>
            <MuiTextField
              inputProps={{
                maxLength: 300,
              }}
              onChange={e => {
                setDealNotes(e.target.value);
              }}
              value={dealNotes}
            />
          </MuiFormControl>
        </MuiBox>

        <MuiBox>
          {ENUMS.SERVICE_DEAL_TYPE.FULL_PREMIUM_SERVICES === selectedDealType &&
            !!selectedGroupWithRecoup?.recoupAccounts?.length && (
              <>
                <h2>Select Recoup Accounts to Associate with this Deal</h2>
                <MuiFormGroup>
                  {selectedGroupWithRecoup?.recoupAccounts.map(r => {
                    const isAssociatedWithAnotherDeal = !!r.service_deal_id && r.service_deal_id !== serviceDealId;
                    return (
                      <MuiBox key={r.id}>
                        <MuiFormControlLabel
                          className={classes.checkBoxElement}
                          control={
                            <MuiCheckbox
                              disabled={isAssociatedWithAnotherDeal}
                              checked={associatedRecoupAccounts[r.id] === true}
                              onChange={event =>
                                setAssociatedRecoupAccounts({
                                  ...associatedRecoupAccounts,
                                  [event.target.name]: !!event.target.checked,
                                })
                              }
                              name={`${r.id}`}
                            />
                          }
                          label={r.name}
                        />
                        {isAssociatedWithAnotherDeal && (
                          <MuiBox component="span">
                            (Currently associated with &nbsp;
                            <Link to={`/service-deals/edit/${r.service_deal_id}`}>
                              another deal: ID {r.service_deal_id}
                            </Link>
                            )
                          </MuiBox>
                        )}
                      </MuiBox>
                    );
                  })}
                </MuiFormGroup>
              </>
            )}
        </MuiBox>
        <MuiBox style={{ opacity: isExpired ? '0.5' : '1' }}>
          <span className={classes.endLabel}>NOT Active</span>
          <MuiFormControlLabel
            className={classes.toggleLabel}
            control={
              <MuiSwitch
                checked={isActive}
                onChange={() => {
                  setIsActive(!isActive);
                }}
                name="isActive"
                inputProps={{ 'aria-label': 'secondary checkbox' }}
                disabled={isExpired}
              />
            }
          />
          <span className={classes.startLabel}>ACTIVE</span>
        </MuiBox>

        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Expiration Date PST Inclusive </MuiFormHelperText>
            <input
              type="date"
              id="open_date"
              onChange={e => {
                setexpirationDate(e.target.value);
              }}
              value={expirationDate}
            />
          </MuiFormControl>
        </MuiBox>

        {!isExpirationDateValid && (
          <MuiBox className={classes.error}>Expiration date is required for an active deal.</MuiBox>
        )}
      </MuiGrid>
    </MuiGrid>
  );
}

const StyledDealForm = withStyles(styles)(DealForm);

function ServiceDealForm(props) {
  const { goBack, id, classes, setToast } = props;

  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedDealType, setSelectedDealType] = useState('');
  const [dealName, setDealName] = useState('');
  const [dealNotes, setDealNotes] = useState('');
  const [opsId, setOpsId] = useState('');

  const [isActive, setIsActive] = useState(false);
  const [expirationDate, setexpirationDate] = useState('');
  const [associatedRecoupAccounts, setAssociatedRecoupAccounts] = useState({});

  const setExpirationDateHandler = expirationDate => {
    if (isDateBeforeToday(expirationDate)) {
      setIsActive(false);
    }
    setexpirationDate(expirationDate);
  };

  const getServiceDealByIdQuery = useQuery(['service-deal', id], () => ServiceDeals.getServiceDealById(id), {
    enabled: !!id,
    refetchOnWindowFocus: false,
    onSuccess: data => {
      setSelectedGroup(data.userGroup.id);
      setDealName(data.name);
      setSelectedDealType(data.service_deal_type);
      setDealNotes(data.notes);
      setOpsId(data.ops_id);
      setIsActive(data.is_active);
      setexpirationDate(data.expiration_date ? isoToExpirationEndDate(data.expiration_date) : '');

      const recoupAccountAssociations =
        data.recoupAccounts?.reduce((acc, cur) => {
          acc[cur.id] = true;

          return acc;
        }, {}) ?? {};

      setAssociatedRecoupAccounts(recoupAccountAssociations);
    },
  });

  const serviceDealMutation = useMutation(
    body => (id ? ServiceDeals.patchServiceDeal(id, body) : ServiceDeals.postServiceDeal(body)),
    {
      onSuccess: () => {
        setToast({ message: 'Success!', alertType: 'success' });
        setTimeout(() => {
          setToast({});
          goBack();
        }, 1500);
      },
      onError: e => {
        setToast({ message: e?.response?.data?.detail || e?.response?.data || 'Error' });
      },
    }
  );

  if (getServiceDealByIdQuery.isLoading || serviceDealMutation.isLoading) {
    return <MuiCircularProgress />;
  }

  const handleOnSubmit = e => {
    e.preventDefault();

    const recoupAccountIds = [];
    for (const [recoupAccountId, checked] of Object.entries(associatedRecoupAccounts)) {
      if (checked === true) recoupAccountIds.push(Number(recoupAccountId));
    }

    const mutateBody = {
      recoupAccountIds,
      serviceDeal: {
        name: dealName,
        notes: dealNotes,
        ops_id: opsId,
        service_deal_type: selectedDealType,
        user_group_id: selectedGroup,
        is_active: isActive,
        expiration_date: expirationDate ? dateToExpirationDateTime(expirationDate) : null,
      },
    };

    serviceDealMutation.mutate(mutateBody);
  };

  const isExpirationDateValid = !isActive || !!expirationDate;

  const isValid = selectedGroup && selectedDealType && dealName && isExpirationDateValid;

  return (
    <form onSubmit={handleOnSubmit} className={classes.form}>
      <StyledDealForm
        serviceDealId={parseInt(id, 10)}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        selectedDealType={selectedDealType}
        setSelectedDealType={setSelectedDealType}
        dealName={dealName}
        setDealName={setDealName}
        dealNotes={dealNotes}
        setDealNotes={setDealNotes}
        opsId={opsId}
        setOpsId={setOpsId}
        isActive={isActive}
        setIsActive={setIsActive}
        expirationDate={expirationDate}
        setexpirationDate={setExpirationDateHandler}
        associatedRecoupAccounts={associatedRecoupAccounts}
        setAssociatedRecoupAccounts={setAssociatedRecoupAccounts}
        isExpirationDateValid={isExpirationDateValid}
      />
      <MuiGrid item xs={12}>
        <MuiBox>
          <MuiButton onClick={goBack}>Cancel</MuiButton>
          <MuiButton variant="contained" size="small" type="submit" disabled={!isValid}>
            Save
          </MuiButton>
        </MuiBox>
      </MuiGrid>
    </form>
  );
}

export default withStyles(styles)(ServiceDealForm);
